/**
 * Operations panel of `/settings/tenant` (#2366) — `GET/PUT
 * /api/tenant/settings` (#2364).
 *
 * One row per setting: its effective value and where it comes from — saved on
 * the tenant, or not set on the tenant. The row's
 * Pencil opens an input in the value cell (#2429), one row at a time, with
 * "Save", "Cancel" and, for a tenant value, "Reset to default".
 *
 * The input is prefilled only with a TENANT value; unset values stay empty. The PUT always carries both
 * settings: the edited one (an emptied input, or "Reset to default", is
 * `null`, which clears that tenant setting) and the other one
 * as it is saved on the tenant — `null` when it is legacy.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input } from '@resource-kit/ui/react';
import type { TenantSettingSource, TenantSettings } from '@/services/tenants';
import { requestErrorMessage, useSaveTenantSettings } from '../hooks/useTenants';
import { EditButton, SettingsPanel, SettingsRow } from './SettingsSection';

const EM_DASH = '—';

const SOURCE_LABEL_KEY = {
  tenant: 'tenants.settings.source.tenant',
  legacy: 'tenants.settings.source.legacy',
} as const satisfies Record<TenantSettingSource, string>;

type SettingField = 'packagingCutoffTime' | 'epochDate';

const FIELDS = [
  { field: 'packagingCutoffTime', type: 'time', labelKey: 'tenants.settings.packagingCutoffTime' },
  { field: 'epochDate', type: 'date', labelKey: 'tenants.settings.epochDate' },
] as const satisfies readonly { field: SettingField; type: string; labelKey: string }[];

/** The value saved on the tenant; `null` when not set on the tenant. */
function tenantValue(setting: TenantSettings[SettingField]): string | null {
  return setting.source === 'tenant' ? setting.value : null;
}

export function TenantSettingsCard({ settings }: { settings: TenantSettings }) {
  const { t } = useTranslation();
  const mutation = useSaveTenantSettings();
  const [editing, setEditing] = useState<SettingField | null>(null);
  const [draft, setDraft] = useState('');

  const open = (field: SettingField) => {
    setEditing(field);
    setDraft(tenantValue(settings[field]) ?? '');
    mutation.reset();
  };
  const close = () => {
    setEditing(null);
    setDraft('');
  };
  const cancel = () => {
    close();
    mutation.reset();
  };
  const save = (field: SettingField, value: string | null) => {
    const body: Record<SettingField, string | null> = {
      packagingCutoffTime: tenantValue(settings.packagingCutoffTime),
      epochDate: tenantValue(settings.epochDate),
    };
    body[field] = value;
    mutation.mutate(body, { onSuccess: close });
  };

  return (
    <SettingsPanel testId="tenant-settings-card">
      {FIELDS.map(({ field, type, labelKey }) => {
        const setting = settings[field];
        const id = `tenant-setting-${field}`;
        const label = t(labelKey);
        const isEditing = editing === field;
        return (
          <SettingsRow
            key={field}
            testId={`tenant-setting-row-${field}`}
            label={label}
            labelFor={isEditing ? id : undefined}
            action={
              isEditing ? (
                <>
                  {setting.source === 'tenant' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={mutation.isPending}
                      onClick={() => save(field, null)}
                    >
                      {t('tenants.settings.resetToDefault')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={mutation.isPending}
                    onClick={cancel}
                  >
                    {t('tenants.settings.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={mutation.isPending}
                    onClick={() => save(field, draft.trim() || null)}
                  >
                    {mutation.isPending ? t('tenants.settings.saving') : t('tenants.settings.save')}
                  </Button>
                </>
              ) : (
                <EditButton
                  label={t('tenants.settings.edit', { label })}
                  onClick={() => open(field)}
                />
              )
            }
          >
            {isEditing ? (
              <div className="space-y-2">
                <Input
                  id={id}
                  type={type}
                  className="h-9 max-w-48"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                {mutation.isError && (
                  <p className="text-sm text-destructive">
                    {t('tenants.settings.saveFailed', {
                      message: requestErrorMessage(mutation.error),
                    })}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{setting.value ?? EM_DASH}</span>
                <Badge variant={setting.source === 'tenant' ? 'success' : 'secondary'}>
                  {t(SOURCE_LABEL_KEY[setting.source])}
                </Badge>
              </div>
            )}
          </SettingsRow>
        );
      })}
      {editing === null && mutation.isSuccess && (
        <p className="px-5 py-2 text-sm text-success">{t('tenants.settings.saved')}</p>
      )}
    </SettingsPanel>
  );
}
